import { useState, useEffect, useCallback } from 'react';
import { Box, Container, Typography, Paper, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import RequestServicesForm from '../components/RequestServicesForm';

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: string | HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        }
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAAFReJ_RueO0t9k9f';

function RequestServicesPage() {
  const [turnstileToken, setTurnstileToken] = useState('');

  const initTurnstile = useCallback(() => {
    if (window.turnstile) {
      const container = document.getElementById('turnstile-widget-request');
      if (container && container.children.length === 0) {
        window.turnstile.render('#turnstile-widget-request', {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => {
            setTurnstileToken(token);
          },
          'expired-callback': () => {
            setTurnstileToken('');
          },
          'error-callback': () => {
            setTurnstileToken('');
          },
        });
      }
    }
  }, []);

  useEffect(() => {
    if (window.turnstile) {
      initTurnstile();
    } else {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      script.onload = initTurnstile;
      document.head.appendChild(script);
    }
  }, [initTurnstile]);

  return (
    <Box className="page-shell">
      <Container maxWidth="md" className="page-wrap" sx={{ px: 0 }}>
        <Typography
          variant="h2"
          textAlign="center"
          sx={{ mb: 2, fontWeight: 700 }}
        >
          Request Services
        </Typography>
        <Typography
          variant="body1"
          textAlign="center"
          color="text.secondary"
          sx={{ mb: 4 }}
        >
          Tell us about your project and we'll get back to you with a plan.
        </Typography>

        <Paper sx={{ p: { xs: 3, md: 4 } }}>
          <RequestServicesForm turnstileToken={turnstileToken} />
        </Paper>

        <Typography
          variant="body2"
          textAlign="center"
          color="text.secondary"
          sx={{ mt: 4 }}
        >
          By submitting this form, you agree to our{' '}
          <Link component={RouterLink} to="/privacy">
            Privacy Policy
          </Link>{' '}
          and{' '}
          <Link component={RouterLink} to="/terms">
            Terms & Conditions
          </Link>
          .
        </Typography>
      </Container>
    </Box>
  );
}

export default RequestServicesPage;
